import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, FileText, MessageSquare, ChevronLeft, ChevronRight, Home, RefreshCw, CheckCircle } from 'lucide-react';
import * as cacheManager from '../utils/cacheManager';

// Receive cleaners, properties, owners from App.js (already cached there)
const Payouts = ({ cleaners: initialCleaners = [], properties: initialProperties = [], owners: initialOwners = [], preloaded = false }) => {
  // Local state - initialized from props
  const [cleaners] = useState(initialCleaners);
  const [owners, setOwners] = useState(initialOwners);
  const [properties] = useState(initialProperties);

  // Week-specific state - now using PayoutSlots
  const [cleanerSlots, setCleanerSlots] = useState([]);
  const [ownerSlots, setOwnerSlots] = useState([]);

  // Sync owners from props if they change
  useEffect(() => {
    if (initialOwners.length > 0) {
      setOwners(initialOwners);
    }
  }, [initialOwners]);

  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading] = useState(false);
  const [dataFreshness, setDataFreshness] = useState(null); // null, 'loading', 'fresh', 'stored', 'error'

  // Refs for preloading
  const preloadedWeeks = useRef(new Set());
  const isPreloading = useRef(false);

  // Modal states
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [currentCleaner, setCurrentCleaner] = useState(null);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // Forms
  const [noteForm, setNoteForm] = useState({ name: '', amount: '' });
  const [ownerForm, setOwnerForm] = useState({ name: '', assignedProperties: [], isCleaner: false });

  // Calculate week date range
  const getWeekDateRange = useCallback((offset = 0) => {
    const today = new Date();
    let startDate = new Date(today);
    startDate.setDate(startDate.getDate() + (offset * 7));

    // Adjust to Saturday
    while (startDate.getDay() !== 6) {
      startDate.setDate(startDate.getDate() - 1);
    }
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  }, []);

  // Check if week is completed (past)
  const isWeekCompleted = useCallback((offset) => {
    const { endDate } = getWeekDateRange(offset);
    return endDate < new Date();
  }, [getWeekDateRange]);

  // Fetch fresh data from API
  const fetchFreshPayoutSlots = useCallback(async (offset) => {
    const weekKey = cacheManager.getWeekKey(offset);
    const weekStart = cacheManager.getWeekStart(offset);
    const isCompleted = isWeekCompleted(offset);
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/payout-slots?weekStart=${weekStart.toISOString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch payout slots: ${response.status}`);
      }

      const data = await response.json();

      // Cache to localStorage
      cacheManager.setPayoutSlots(weekKey, data);

      // Update state
      setCleanerSlots(data.cleaners || []);
      setOwnerSlots(data.owners || []);
      setDataFreshness(isCompleted ? 'stored' : 'fresh');

      console.log(`[Payouts] Loaded ${data.cleaners?.length || 0} cleaner slots, ${data.owners?.length || 0} owner slots for ${weekKey}`);

    } catch (err) {
      console.error('[Payouts] Error fetching slots:', err);
      // Clear any previously displayed week and flag the failure — leaving
      // the old slots up would show the wrong week's payout amounts under a
      // "Live" badge
      setCleanerSlots([]);
      setOwnerSlots([]);
      setDataFreshness('error');
    } finally {
      setLoading(false);
    }
  }, [isWeekCompleted]);

  // Load payout slots from cache or API
  const loadPayoutSlots = useCallback(async (offset, forceRefresh = false) => {
    const weekKey = cacheManager.getWeekKey(offset);
    const isCompleted = isWeekCompleted(offset);

    // 1. Check localStorage first (instant display)
    if (!forceRefresh) {
      const cached = cacheManager.getPayoutSlots(weekKey);
      if (cached && cacheManager.isCacheValid(cached.timestamp)) {
        console.log(`[Payouts] Using cached data for ${weekKey}`);
        setCleanerSlots(cached.cleaners || []);
        setOwnerSlots(cached.owners || []);
        setDataFreshness(isCompleted ? 'stored' : 'fresh');
        return;
      }
    }

    // 2. Fetch from MongoDB
    await fetchFreshPayoutSlots(offset);
  }, [isWeekCompleted, fetchFreshPayoutSlots]);


  // Preload adjacent weeks
  const preloadAdjacentWeeks = useCallback(async () => {
    if (isPreloading.current) return;

    isPreloading.current = true;

    const offsets = [weekOffset - 1, weekOffset + 1];

    for (const offset of offsets) {
      const weekKey = cacheManager.getWeekKey(offset);

      if (preloadedWeeks.current.has(weekKey)) continue;

      try {
        const cached = cacheManager.getPayoutSlots(weekKey);
        if (!cached) {
          const weekStart = cacheManager.getWeekStart(offset);
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/reservations/payout-slots?weekStart=${weekStart.toISOString()}`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
            }
          );
          if (response.ok) {
            const data = await response.json();
            cacheManager.setPayoutSlots(weekKey, data);
          }
        }
        preloadedWeeks.current.add(weekKey);
      } catch (error) {
        console.error(`[Payouts] Error preloading week ${offset}:`, error);
      }
    }

    isPreloading.current = false;
  }, [weekOffset]);

  // Fetch week data when component mounts or week changes
  useEffect(() => {
    loadPayoutSlots(weekOffset);

    // Start preloading adjacent weeks after a short delay
    const timer = setTimeout(() => preloadAdjacentWeeks(), 100);
    return () => clearTimeout(timer);
  }, [weekOffset, loadPayoutSlots, preloadAdjacentWeeks]);

  // Refresh when an owner-report tab (opened from here) saves cost changes
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'costs-updated') {
        cacheManager.clearPayoutSlots(cacheManager.getWeekKey(weekOffset));
        loadPayoutSlots(weekOffset, true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [weekOffset, loadPayoutSlots]);

  // Week navigation
  const navigateWeek = (newOffset) => {
    setWeekOffset(newOffset);
  };


  // Generate tooltip content for cleaner (from pre-computed breakdown)
  const generateCleanerTooltip = (slot) => {
    if (!slot.breakdown || slot.breakdown.length === 0) {
      return 'No checkouts this week';
    }

    const lines = slot.breakdown.map(item => {
      const daysInfo = item.assignedDays && item.assignedDays.length > 0
        ? ` (${item.assignedDays.map(d => d.charAt(0).toUpperCase() + d.charAt(1)).join(',')})`
        : '';
      return `${item.propertyName}${daysInfo}: $${item.cleaningFee} x ${item.checkouts} = $${item.amount.toFixed(2)}`;
    });

    // Add notes if present
    if (slot.notes && slot.notes.length > 0) {
      slot.notes.forEach(note => {
        lines.push(`${note.name}: $${note.amount.toFixed(2)}`);
      });
    }

    return lines.join('\n');
  };

  // Show tooltip
  const showTooltip = (e, content) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      show: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  // Hide tooltip
  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  // Format property assignments for display
  const formatPropertyAssignments = (assignments) => {
    if (!assignments || assignments.length === 0) {
      return <span className="text-xs text-gray-500">No properties assigned</span>;
    }

    return assignments.map((assignment, index) => {
      const property = assignment.propertyId || assignment;
      const assignedDaysCount = assignment.assignedDays?.length || 0;
      const isPartialAssignment = assignedDaysCount > 0 && assignedDaysCount < 7;

      return (
        <span key={property._id || index}>
          {property.name}
          {isPartialAssignment && '*'}
          {index < assignments.length - 1 && ' • '}
        </span>
      );
    }).filter(Boolean);
  };

  // Delete cleaner note
  const deleteCleanerNote = async () => {
    try {
      const { startDate } = getWeekDateRange(weekOffset);
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/cleanerNotes?cleanerId=${currentCleaner._id}&weekStart=${startDate.toISOString()}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      const weekKey = cacheManager.getWeekKey(weekOffset);
      cacheManager.clearPayoutSlots(weekKey);
      await loadPayoutSlots(weekOffset, true);

      setIsNoteModalOpen(false);
      setNoteForm({ name: '', amount: '' });
    } catch (error) {
      console.error('[Payouts] Error deleting note:', error);
    }
  };

  // Save cleaner note
  const saveCleanerNote = async () => {
    const amount = parseFloat(noteForm.amount);
    if (!noteForm.name.trim() || isNaN(amount)) {
      alert('Please enter a note name and a valid amount.');
      return;
    }

    try {
      const { startDate } = getWeekDateRange(weekOffset);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cleanerNotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          cleanerId: currentCleaner._id,
          weekStart: startDate.toISOString(),
          name: noteForm.name,
          amount
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save note (${response.status})`);
      }

      // Clear cache and refresh
      const weekKey = cacheManager.getWeekKey(weekOffset);
      cacheManager.clearPayoutSlots(weekKey);
      await loadPayoutSlots(weekOffset, true);

      setIsNoteModalOpen(false);
      setNoteForm({ name: '', amount: '' });
    } catch (error) {
      console.error('[Payouts] Error saving note:', error);
      alert('Failed to save note. Please try again.');
    }
  };

  // Delete owner
  const deleteOwner = async (ownerId) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/owners/${ownerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete owner (${response.status})`);
      }

      // Invalidate App's bootstrap cache — its owners list feeds this
      // component's props on remount and would resurrect the deleted owner
      localStorage.removeItem('app_bootstrap_cache');

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/owners`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (res.ok) {
        setOwners(await res.json());
      }
    } catch (error) {
      console.error('[Payouts] Error deleting owner:', error);
      alert('Failed to delete owner. Please try again.');
    }
  };

  // Save owner
  const saveOwner = async (e) => {
    e.preventDefault();
    try {
      const url = currentOwner
        ? `${process.env.REACT_APP_API_URL}/api/owners/${currentOwner._id}`
        : `${process.env.REACT_APP_API_URL}/api/owners`;

      const response = await fetch(url, {
        method: currentOwner ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(ownerForm)
      });

      if (!response.ok) {
        throw new Error(`Failed to save owner (${response.status})`);
      }

      // Keep App's bootstrap cache in sync — see deleteOwner
      localStorage.removeItem('app_bootstrap_cache');

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/owners`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (res.ok) {
        setOwners(await res.json());
      }
      setIsOwnerModalOpen(false);
    } catch (error) {
      console.error('[Payouts] Error saving owner:', error);
      alert('Failed to save owner. Please try again.');
    }
  };

  // Open owner report
  const openOwnerReport = (owner) => {
    const { startDate } = getWeekDateRange(weekOffset);
    const json = JSON.stringify({
      ownerId: owner._id,
      ownerName: owner.name,
      weekStart: startDate.toISOString(),
      weekOffset
    });
    // Byte-accurate base64 encode — plain btoa throws on non-Latin1 owner
    // names; OwnerReport decodes with the matching TextDecoder logic
    const encoded = btoa(String.fromCharCode(...new TextEncoder().encode(json)));
    window.open(`/owner-report/${encoded}`, '_blank');
  };

  // Find slot for a cleaner by personId
  const findCleanerSlot = (cleanerId) => {
    return cleanerSlots.find(s => s.personId === cleanerId || s.personId?.toString() === cleanerId?.toString());
  };

  // Find slot for an owner by personId
  const findOwnerSlot = (ownerId) => {
    return ownerSlots.find(s => s.personId === ownerId || s.personId?.toString() === ownerId?.toString());
  };

  // Calculate totals from pre-computed slots
  const totals = {
    cleaners: cleanerSlots.reduce((sum, s) => sum + (s.totalPayout || 0), 0),
    owners: ownerSlots.filter(s => !s.isCleaner).reduce((sum, s) => sum + (s.totalPayout || 0), 0)
  };

  // Format date range
  const { startDate, endDate } = getWeekDateRange(weekOffset);
  const dateRange = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

  // Week indicator styling
  const getWeekStyle = () => {
    if (weekOffset === 0) return 'bg-emerald-950/30 border-emerald-700';
    if (weekOffset < 0) return 'bg-zinc-950 border-zinc-700';
    return 'bg-violet-950/30 border-violet-700';
  };

  // Data freshness indicator
  const getFreshnessIndicator = () => {
    if (backgroundLoading) {
      return (
        <span className="text-xs bg-yellow-900/30 text-yellow-400 border border-yellow-800 px-2 py-0.5 rounded-md flex items-center gap-1">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Updating...
        </span>
      );
    }

    if (dataFreshness === 'fresh') {
      return (
        <span className="text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-800 px-2 py-0.5 rounded-md flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Live
        </span>
      );
    }

    if (dataFreshness === 'stored') {
      return (
        <span className="text-xs bg-zinc-800 text-zinc-500 border border-zinc-700 px-2 py-0.5 rounded-md">
          Stored
        </span>
      );
    }

    if (dataFreshness === 'error') {
      return (
        <span className="text-xs bg-red-900/30 text-red-400 border border-red-800 px-2 py-0.5 rounded-md">
          Failed to load
        </span>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col relative bg-zinc-950">
      {/* Tooltip */}
      {tooltip.show && (
        <div
          className="fixed z-50 bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs rounded-lg px-3 py-2 pointer-events-none whitespace-pre-line shadow-xl"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            maxWidth: '300px'
          }}
        >
          {tooltip.content}
        </div>
      )}

      {/* Header with week navigation */}
      <div className={`border-b-2 p-4 flex justify-between items-center ${getWeekStyle()}`}>
        <button
          onClick={() => navigateWeek(weekOffset - 1)}
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors"
          disabled={loading}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="text-center">
          <h2 className="text-base font-semibold text-zinc-100">
            {weekOffset === 0 ? 'Current Week' : weekOffset < 0 ? 'Past Week' : 'Future Week'}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">{dateRange}</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            {getFreshnessIndicator()}
            {weekOffset !== 0 && (
              <button
                onClick={() => navigateWeek(0)}
                className="text-xs px-2 py-0.5 rounded-md flex items-center gap-1 bg-violet-900/30 text-violet-400 border border-violet-800 hover:bg-violet-900/50 transition-colors"
              >
                <Home className="w-3 h-3" />
                Current Week
              </button>
            )}
          </div>
        </div>

        <button
          onClick={() => navigateWeek(weekOffset + 1)}
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors"
          disabled={loading}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {loading && cleanerSlots.length === 0 && ownerSlots.length === 0 && (
        <div className="absolute inset-0 bg-zinc-950/80 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
        </div>
      )}

      <div className="p-4">
        <div className="payouts-grid grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Cleaners Section */}
          <div className="bg-zinc-800 rounded-xl border border-zinc-700">
            <div className="border-b border-zinc-700 p-3 flex justify-between items-center">
              <h3 className="text-sm font-semibold text-zinc-100">Cleaners</h3>
              <span className="text-sm font-medium text-emerald-400">
                Total: ${totals.cleaners.toFixed(2)}
              </span>
            </div>
            <div className="p-3 space-y-1">
              {cleaners.map(cleaner => {
                const slot = findCleanerSlot(cleaner._id);
                const payout = slot?.totalPayout || 0;
                const hasNote = slot?.notes && slot.notes.length > 0;

                return (
                  <div key={cleaner._id} className="flex justify-between items-center p-2 hover:bg-zinc-800/50 rounded-lg transition-colors">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-zinc-200">{cleaner.name}</div>
                      <div className="text-xs text-zinc-600">
                        {formatPropertyAssignments(cleaner.assignedProperties)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-sm font-medium text-zinc-300 cursor-help"
                        onMouseEnter={(e) => slot && showTooltip(e, generateCleanerTooltip(slot))}
                        onMouseLeave={hideTooltip}
                      >
                        ${payout.toFixed(2)}
                      </span>
                      <button
                        onClick={() => {
                          setCurrentCleaner(cleaner);
                          const note = slot?.notes?.[0];
                          setNoteForm(note ? { name: note.name, amount: note.amount } : { name: '', amount: '' });
                          setIsNoteModalOpen(true);
                        }}
                        className="text-zinc-500 hover:text-violet-400 relative transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        {hasNote && (
                          <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
              {cleaners.length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-4">No cleaners configured</p>
              )}
            </div>
          </div>

          {/* Owners Section */}
          <div className="bg-zinc-800 rounded-xl border border-zinc-700">
            <div className="border-b border-zinc-700 p-3 flex justify-between items-center">
              <h3 className="text-sm font-semibold text-zinc-100">Owners</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-emerald-400">
                  Total: ${totals.owners.toFixed(2)}
                </span>
                <button
                  onClick={() => {
                    setCurrentOwner(null);
                    setOwnerForm({ name: '', assignedProperties: [], isCleaner: false });
                    setIsOwnerModalOpen(true);
                  }}
                  className="text-zinc-500 hover:text-violet-400 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-3 space-y-1">
              {owners.map(owner => {
                const slot = findOwnerSlot(owner._id);
                const payout = slot?.totalPayout || 0;

                return (
                  <div key={owner._id} className="flex justify-between items-center p-2 hover:bg-zinc-800/50 rounded-lg transition-colors">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-zinc-200 flex items-center gap-1.5">
                        {owner.name}
                        {owner.isCleaner && (
                          <span className="text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-800 px-1.5 py-0.5 rounded-md">
                            Cleaner
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-600">
                        {owner.assignedProperties?.map(p => p.name).join(', ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-300">
                        ${payout.toFixed(2)}
                      </span>
                      <button
                        onClick={() => {
                          setCurrentOwner(owner);
                          setOwnerForm({
                            name: owner.name,
                            assignedProperties: owner.assignedProperties.map(p => p._id),
                            isCleaner: owner.isCleaner
                          });
                          setIsOwnerModalOpen(true);
                        }}
                        className="text-zinc-500 hover:text-violet-400 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openOwnerReport(owner)}
                        className="text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteOwner(owner._id)}
                        className="text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {owners.length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-4">No owners configured</p>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 bg-zinc-900 rounded-xl border border-zinc-800 p-4">
          <div className="flex justify-around text-center">
            <div>
              <div className="text-2xl font-bold text-emerald-400">
                ${(totals.cleaners + totals.owners).toFixed(2)}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">Total Payouts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Cleaner Note Modal */}
      {isNoteModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl max-w-md w-full p-6">
            <h3 className="text-base font-semibold text-zinc-100 mb-4">
              Note for {currentCleaner?.name}
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Description"
                value={noteForm.name}
                onChange={(e) => setNoteForm({ ...noteForm, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
              />
              <input
                type="number"
                placeholder="Amount"
                value={noteForm.amount}
                onChange={(e) => setNoteForm({ ...noteForm, amount: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                step="0.01"
              />
              <div className="flex justify-between items-center pt-2">
                {noteForm.name || noteForm.amount ? (
                  <button
                    onClick={deleteCleanerNote}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-950/30 border border-red-900/50 transition-colors"
                  >
                    Delete
                  </button>
                ) : <span />}
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsNoteModalOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border border-zinc-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCleanerNote}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Owner Modal */}
      {isOwnerModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl max-w-md w-full p-6">
            <h3 className="text-base font-semibold text-zinc-100 mb-4">
              {currentOwner ? 'Edit Owner' : 'Add Owner'}
            </h3>
            <form onSubmit={saveOwner} className="space-y-3">
              <input
                type="text"
                placeholder="Name"
                value={ownerForm.name}
                onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                required
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ownerForm.isCleaner}
                  onChange={(e) => setOwnerForm({ ...ownerForm, isCleaner: e.target.checked })}
                  className="h-4 w-4 text-violet-600 border-zinc-600 rounded bg-zinc-800"
                />
                <span className="text-sm text-zinc-300">Also a cleaner</span>
              </label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Properties</div>
                {properties.map(property => (
                  <label key={property._id} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ownerForm.assignedProperties.includes(property._id)}
                      onChange={(e) => {
                        const props = e.target.checked
                          ? [...ownerForm.assignedProperties, property._id]
                          : ownerForm.assignedProperties.filter(id => id !== property._id);
                        setOwnerForm({ ...ownerForm, assignedProperties: props });
                      }}
                      className="h-4 w-4 text-violet-600 border-zinc-600 rounded bg-zinc-800"
                    />
                    <span className="text-sm text-zinc-300">{property.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsOwnerModalOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border border-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts;
